<!-- eslint-disable vue/multi-word-component-names, vue/require-toggle-inside-transition -->
<template>
	<div
		class="fixed inset-0 z-9998! overflow-y-auto"
		aria-labelledby="modal-title"
		role="dialog"
		aria-modal="true"
		@wheel="handleScroll"
		@touchmove="handleScroll"
		@scroll="handleScroll"
	>
		<div
			class="flex min-h-full justify-center text-center sm:block sm:p-0"
			:class="centerOnMobile ? 'items-center p-3' : ['items-end', removePadding ? '' : 'px-4 pt-4 pb-20']"
		>
			<!--
            Background overlay, show/hide based on modal state.

            Entering: "ease-out duration-300"
                From: "opacity-0"
                To: "opacity-100"
            Leaving: "ease-in duration-200"
                From: "opacity-100"
                To: "opacity-0"
            -->
			<transition
				enter-active-class="ease-out duration-300"
				leave-active-class="ease-in duration-200"
				enter-class="opacity-0"
				enter-to-class="opacity-100"
				leave-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div
					class="fixed inset-0 bg-black/50 transition-opacity"
					aria-hidden="true"
					data-testid="modal-backdrop"
					@click="handleBackdropClick"
				/>
			</transition>

			<span
				class="hidden sm:inline-block sm:h-screen sm:align-middle"
				aria-hidden="true"
			>&#8203;</span>

			<transition
				enter-active-class="ease-out duration-300"
				leave-active-class="ease-in duration-200"
				enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
				enter-to-class="opacity-100 translate-y-0 sm:scale-100"
				leave-class="opacity-100 translate-y-0 sm:scale-100"
				leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
			>
				<div
					:class="[cssWidth, cssRoundedSize]"
					class="
						relative inline-block w-full overflow-hidden bg-white text-left
						align-bottom shadow-xl transition-all sm:my-8 sm:align-middle
					"
				>
					<div
						v-if="isNewMark"
						class="absolute top-0 mt-[10px]  flex w-full items-center justify-center"
					>
						<div class="newLabel w-[101px] text-center">
							<span class="px-7 font-[Inter] text-sm font-semibold text-[#FFFFFF]">
								New
							</span>
						</div>
					</div>
					<div
						v-if="closeButtonAbsolute"
						class="absolute top-4 right-4 z-10"
					>
						<span
							v-if="allowClose"
							class="block max-md:tap-target"
							data-testid="modal-close"
							@click="closeModal"
						>
							<XMarkIcon
								class="size-6 cursor-pointer text-won-dark-blue"
							/>
						</span>
					</div>
					<div
						v-else
						class="flex justify-end"
						:class="[removePadding ? '': 'px-4 pt-4']"
					>
						<span
							v-if="allowClose"
							class="block max-md:tap-target"
							data-testid="modal-close"
							@click="closeModal"
						>
							<XMarkIcon
								class="size-6 cursor-pointer text-won-dark-blue"
							/>
						</span>
					</div>
					<div
						class="mb-2 bg-white"
						:class="[removePadding ? '': 'px-6 pb-4 sm:pb-4']"
					>
						<div
							class="sm:text-left"
							:class="[removePadding ? '': 'mt-3 sm:mt-0 ']"
						>
							<slot name="header" />
							<div :class="[removePadding ? '': 'mt-5']">
								<slot name="body" />
							</div>
						</div>
					</div>
					<div
						v-if="!hideActions"
						class="flex flex-col space-y-2 p-6 sm:flex sm:flex-row sm:justify-between sm:space-y-0"
					>
						<slot name="actions" />
					</div>
				</div>
			</transition>
		</div>
	</div>
</template>

<script setup>
import { onMounted, computed, onBeforeUnmount, toRefs, watch } from 'vue';

import { XMarkIcon } from '@heroicons/vue/24/outline';

const emit = defineEmits(['modal:close']);
const props = defineProps({
	isNewMark: {
		type: Boolean,
		default: false,
	},
	preventScrolling: {
		type: Boolean,
		default: true,
	},
	hideActions: {
		type: Boolean,
		default: false,
	},
	allowClose: {
		type: Boolean,
		default: true,
	},
	maxWidth: {
		type: String,
		default: null,
	},
	roundedSize: {
		type: String,
		default: undefined,
	},
	removePadding: {
		type: Boolean,
		default: false
	},
	/** Centre the dialog on small screens too, instead of docking it to the bottom (EN-1025). */
	centerOnMobile: {
		type: Boolean,
		default: false,
	},
	closeIconColor: {
		type: String,
		default: 'black',
	},
	closeButtonAbsolute: {
		type: Boolean,
		default: false,
	},
	/**
	 * Close on a click outside the dialog (EN-1183). Opt-in: many consumers hold form input that a
	 * stray click would discard.
	 */
	closeOnBackdrop: {
		type: Boolean,
		default: false,
	}
});

const {
	isNewMark,
	preventScrolling,
	hideActions,
	allowClose,
	maxWidth,
	roundedSize,
	removePadding,
	closeButtonAbsolute,
	closeOnBackdrop
} = toRefs(props);

const closeModal = () => emit('modal:close');

const handleEscape = (e) => {
	if (e.keyCode === 27 && allowClose.value) {
		closeModal();
	}
};

const handleBackdropClick = () => {
	if (closeOnBackdrop.value && allowClose.value) {
		closeModal();
	}
};

onMounted(() => {
	document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
	document.removeEventListener('keydown', handleEscape);
});

const handleScroll = (e) => {
	if (preventScrolling.value) {
		e.preventDefault();
		return false;
	}
};

const cssWidth = computed(() => {
	return maxWidth.value ?? 'sm:max-w-md';
});

watch(maxWidth, (newValue) => {
	cssWidth.value = newValue;
});

const cssRoundedSize = computed(() => {
	return (roundedSize.value ?? 'rounded-xl');
});
</script>

<style scoped>
  .newLabel {
    background: linear-gradient(180deg, #073B5D 0%, #2A8176 100%);
    border-radius: 44px;
  }
</style>
